import { X, Download, Columns3, RotateCcw, BookmarkCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Table } from '@tanstack/react-table'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import { Label } from '@/shared/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import type { FilterValue } from '@/shared/lib/api/api-types'

interface DataTableToolbarProps<TData> {
  table: Table<TData>
  onExport?: () => void
  hasSavedView?: boolean
  onResetView?: () => void
}

function getFilterLabel(value: FilterValue, labelOf?: (value: string) => string): string {
  if (value.filterType === 'set') return value.values.map((filterValue) => labelOf?.(filterValue) ?? filterValue).join(', ')
  if (value.filterType === 'text') return `${value.type}: ${value.filter}`
  if (value.filterType === 'number')
    return value.filterTo !== undefined
      ? `${value.filter} – ${value.filterTo}`
      : `${value.type}: ${value.filter}`
  if (value.filterType === 'date')
    return value.dateTo ? `${value.dateFrom} – ${value.dateTo}` : value.dateFrom
  return ''
}

export function DataTableToolbar<TData>({
  table,
  onExport,
  hasSavedView,
  onResetView,
}: DataTableToolbarProps<TData>) {
  const { t } = useTranslation()
  const activeFilters = table.getState().columnFilters
  const hasFilters = activeFilters.length > 0
  const allColumns = table.getAllColumns().filter((column) => column.getCanHide())

  const [columnsOpen, setColumnsOpen] = useState(false)
  const [draft, setDraft] = useState<Record<string, boolean>>({})

  const allSelected = allColumns.every((column) => draft[column.id] ?? column.getIsVisible())

  function openColumns() {
    const initial: Record<string, boolean> = {}
    allColumns.forEach((column) => { initial[column.id] = column.getIsVisible() })
    setDraft(initial)
    setColumnsOpen(true)
  }

  function applyColumns() {
    table.setColumnVisibility(draft)
    setColumnsOpen(false)
  }

  function cancelColumns() {
    setColumnsOpen(false)
  }

  function toggleAll(value: boolean) {
    const next: Record<string, boolean> = {}
    allColumns.forEach((column) => { next[column.id] = value })
    setDraft(next)
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {hasSavedView && (
        <Badge
          variant="outline"
          className="h-6 gap-1 px-2 text-xs font-normal text-muted-foreground"
          aria-label={t('dataTable.savedViewActive')}
        >
          <BookmarkCheck className="h-3 w-3" />
          {t('dataTable.savedView')}
        </Badge>
      )}

      {/* Active filter badges */}
      {activeFilters.map((filter) => {
        const column = table.getColumn(filter.id)
        const label =
          column?.columnDef.header?.toString() ??
          column?.id ??
          filter.id
        const value = filter.value as FilterValue
        return (
          <Badge
            key={filter.id}
            variant="secondary"
            className="h-6 gap-1 pl-2 pr-1 text-xs font-normal"
          >
            <span className="font-medium">{label}:</span>
            <span className="max-w-32 truncate">
              {getFilterLabel(value, column?.columnDef.meta?.filterOptionLabel)}
            </span>
            <button
              className="ml-0.5 rounded-sm opacity-60 hover:opacity-100"
              onClick={() => column?.setFilterValue(undefined)}
              aria-label={t('common.removeFilter', { label: String(label) })}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )
      })}

      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-muted-foreground"
          onClick={() => table.resetColumnFilters()}
        >
          <X className="mr-1 h-3 w-3" />
          {t('common.clearFilters')}
        </Button>
      )}

      {hasSavedView && onResetView && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-muted-foreground"
          onClick={onResetView}
        >
          <RotateCcw className="mr-1 h-3 w-3" />
          {t('dataTable.resetView')}
        </Button>
      )}

      <div className="ml-auto flex items-center gap-1">
        {/* Column visibility */}
        <DropdownMenu open={columnsOpen} onOpenChange={(open) => { if (open) openColumns(); else setColumnsOpen(false) }}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
              <Columns3 className="mr-1.5 h-3.5 w-3.5" />
              {t('dataTable.columns')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="flex items-center justify-between px-2 py-1.5">
              <DropdownMenuLabel className="p-0 text-xs">{t('dataTable.columns')}</DropdownMenuLabel>
              <div className="flex items-center gap-1.5">
                <Label htmlFor="columns-toggle-all" className="text-xs text-muted-foreground cursor-pointer">
                  {allSelected ? t('dataTable.deselectAll') : t('dataTable.selectAll')}
                </Label>
                <Switch
                  id="columns-toggle-all"
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  className="h-4 w-7 [&>span]:h-3 [&>span]:w-3"
                />
              </div>
            </div>
            <DropdownMenuSeparator />
            {allColumns.map((column) => (
              <DropdownMenuCheckboxItem
                key={column.id}
                className="text-xs capitalize"
                checked={draft[column.id] ?? column.getIsVisible()}
                onCheckedChange={(checked) => setDraft((prev) => ({ ...prev, [column.id]: checked }))}
                onSelect={(e) => e.preventDefault()}
              >
                {column.columnDef.header?.toString() ?? column.id}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <div className="flex items-center justify-end gap-1 px-2 py-1.5">
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={cancelColumns}>
                {t('dataTable.cancel')}
              </Button>
              <Button size="sm" className="h-6 px-2 text-xs" onClick={applyColumns}>
                {t('dataTable.apply')}
              </Button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Export */}
        {onExport && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onExport}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {t('dataTable.export')}
          </Button>
        )}
      </div>
    </div>
  )
}
